import { stripHtmlToText } from "@salestrak/utilities";
import { z } from "zod";

import { currencyFractionDigits, fractionDigitsOf } from "./currencies.ts";
import { isDayString, toDayString } from "./date-kind.ts";
import { numberDecimalPlaces } from "./number-format.ts";
import { FIELD_TYPES } from "./field-types.ts";
import type { CustomFieldSpec } from "./spec.ts";
import { isValidPhoneValue } from "./phone-format.ts";
import { isValidUrlText } from "./url-format.ts";

/** The email FIELD's format rule: a full address, `local@domain.tld`. The
 *  domain needs at least one dot, every dot must be followed by a label
 *  (`a@d.` is not done yet), and the last label has 2+ characters, so `a@d`
 *  and `a@d.c` are refused. The live editing ring, the commit gate and the
 *  API read the SAME predicate, so a draft the ring cleared can never be
 *  refused at blur or on write. */
export function isValidEmailText(value: string): boolean {
  return /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[^\s@.]{2,}$/.test(value);
}

/** Per-type length caps from the product rule sheet.
 *
 *  Two gates read this and they must agree: the UI gate (`<Input maxLength>`)
 *  and the commit gate (the schemas below). `phone` and `address` are UI-only —
 *  each stores an object, so a character cap cannot apply to the stored value. */
export const CUSTOM_FIELD_MAX_LENGTHS: Readonly<
  Record<string, number | undefined>
> = {
  text: 255,
  paragraph: 50000,
  email: 254,
  phone: 25,
  url: 2000,
  address: 500,
};

const optionalText = z.string().nullish();

const capped = (cap: number | undefined) =>
  cap === undefined
    ? z.string()
    : z.string().max(cap, { error: `Max ${cap} characters` });

/** A number as the form layer produces it: already a number, a numeric string,
 *  or empty. Anything unparseable becomes `null` — "unanswered" — never `NaN`,
 *  which silently defeats every comparison a min or max check makes. */
const numericValue = z.unknown().transform((raw) => {
  if (raw === undefined || raw === null) return raw;
  if (typeof raw === "number") return Number.isNaN(raw) ? null : raw;
  const text = String(raw).trim();
  if (text === "") return null;
  const parsed = Number(text);
  return Number.isNaN(parsed) ? null : parsed;
});

/** An amount as the editor commits it (a number) or as a stored amount reads
 *  back through the form layer (its decimal string). Blank is unanswered. */
const moneyValue = z.unknown().transform((raw, ctx) => {
  if (raw === undefined || raw === null) return raw;
  const parsed =
    typeof raw === "number" ? raw : String(raw).trim() === "" ? null : Number(raw);
  if (parsed === null) return null;
  if (!Number.isFinite(parsed)) {
    ctx.addIssue({ code: "custom", message: "Not a number" });
    return z.NEVER;
  }
  return parsed;
});

/** Applies the field's `min` / `max` config to a numeric schema. Both money and
 *  number carry the pair (the form dialog writes it for either), so both read it. */
function withBounds<T extends z.ZodType<number | null | undefined>>(
  schema: T,
  config: CustomFieldSpec["config"],
): z.ZodType {
  const { min, max } = config ?? {};
  let bounded: z.ZodType<number | null | undefined> = schema;
  if (typeof min === "number") {
    bounded = bounded.refine((value) => value == null || value >= min, {
      error: `Min ${min}`,
    });
  }
  if (typeof max === "number") {
    bounded = bounded.refine((value) => value == null || value <= max, {
      error: `Max ${max}`,
    });
  }
  return bounded;
}

/** The decimal-cap message money and number share: the tooltip sits on the
 *  value, so the field's name is already implied. */
function decimalsMessage(places: number): string {
  return places === 0
    ? "Whole numbers only"
    : `Max ${places} decimal${places === 1 ? "" : "s"}`;
}

function countOf(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

function requiredChoicesOf(
  config: CustomFieldSpec["config"],
): { value: string; label: string }[] {
  const wanted = new Set(config?.requiredOptions ?? []);
  if (wanted.size === 0) return [];
  return (config?.options ?? []).flatMap((option) => {
    const value = option?.value ?? option?.label ?? "";
    return value && wanted.has(value) ? [{ label: option?.label ?? value, value }] : [];
  });
}

export function listOf(labels: readonly string[], joiner: "and" | "or"): string {
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} ${joiner} ${labels[labels.length - 1]}`;
}

export function choiceOptionCount(config: CustomFieldSpec["config"]): number {
  return (config?.options ?? []).filter((option) => (option?.value ?? option?.label ?? "") !== "")
    .length;
}

/** Submit relaxes whichever of Min/Max cannot be met; the builder flags it. */
export function isChoiceCountMet(config: CustomFieldSpec["config"]): boolean {
  const floor = countFloor(config);
  const cap = config?.max;
  if (floor !== null && floor > choiceOptionCount(config)) return false;
  return !(typeof cap === "number" && cap >= 1 && requiredChoicesOf(config).length > cap);
}

/** Core keeps `min` on number and money only, so workspace text/multi-select fields never carry it. */
function countFloor(config: CustomFieldSpec["config"]): number | null {
  const min = config?.min;
  return typeof min === "number" && min >= 1 ? Math.floor(min) : null;
}

export function customFieldMaxLength(field: CustomFieldSpec): number | undefined {
  const cap = CUSTOM_FIELD_MAX_LENGTHS[field.type];
  const own = field.config?.max;
  const texty = field.type === FIELD_TYPES.text || field.type === FIELD_TYPES.paragraph;
  if (!texty || typeof own !== "number" || own < 1) return cap;
  return cap === undefined ? own : Math.min(cap, own);
}

/** The server checks a filling on the filler's calendar (`timeZone`), not its own. */
export function localDayOf(raw: unknown, timeZone?: string): string | null {
  if (isDayString(raw)) return raw;
  const date = raw instanceof Date ? raw : new Date(String(raw));
  if (Number.isNaN(date.getTime())) return null;
  return timeZone ? dayInTimeZone(date, timeZone) : toDayString(date);
}

function dayInTimeZone(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "2-digit",
    timeZone,
    year: "numeric",
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((entry) => entry.type === type)?.value ?? "";
  return `${part("year").padStart(4, "0")}-${part("month")}-${part("day")}`;
}

export function dayFromToday(days: number, timeZone?: string): string {
  if (timeZone) {
    const [y, m, d] = localDayOf(new Date(), timeZone)!.split("-").map(Number);
    return new Date(Date.UTC(y!, m! - 1, d! + days)).toISOString().slice(0, 10);
  }
  const date = new Date();
  date.setDate(date.getDate() + days);
  return localDayOf(date)!;
}

export type DateBound =
  | { kind: "today"; days: number }
  | { kind: "fixed"; value: string };

export function dateBoundsOf(config: CustomFieldSpec["config"]): {
  earliest: DateBound | null;
  latest: DateBound | null;
} {
  const edge = (days: unknown, fixed: unknown): DateBound | null =>
    typeof fixed === "string" && fixed !== ""
      ? { kind: "fixed", value: fixed }
      : typeof days === "number"
        ? { kind: "today", days }
        : null;
  return {
    earliest: edge(config?.min, config?.minDate),
    latest: edge(config?.max, config?.maxDate),
  };
}

/** A fixed day stays a day; an instant bound compares to the millisecond. */
function boundValue(bound: DateBound, timeZone?: string): string | Date {
  return bound.kind === "today" ? dayFromToday(bound.days, timeZone) : bound.value;
}

function onSide(value: unknown, bound: DateBound, side: "after" | "before", timeZone?: string): boolean {
  const edge = boundValue(bound, timeZone);
  const instant = (raw: unknown) =>
    !isDayString(raw) && !(typeof raw === "string" && raw === "")
      ? new Date(raw instanceof Date ? raw.getTime() : String(raw))
      : null;
  const a = instant(value);
  const b = instant(edge);
  if (a && b && !Number.isNaN(a.getTime()) && !Number.isNaN(b.getTime())) {
    return side === "after" ? a.getTime() >= b.getTime() : a.getTime() <= b.getTime();
  }
  const day = localDayOf(value, timeZone);
  const edgeDay = localDayOf(edge, timeZone);
  if (day === null || edgeDay === null) return true;
  return side === "after" ? day >= edgeDay : day <= edgeDay;
}

export function isDateWindowClosed(config: CustomFieldSpec["config"], timeZone?: string): boolean {
  const { earliest, latest } = dateBoundsOf(config);
  if (!earliest || !latest) return false;
  return localDayOf(boundValue(earliest, timeZone), timeZone)! > localDayOf(boundValue(latest, timeZone), timeZone)!;
}

/** None once the window has closed: a window no answer fits would hold Submit for good. */
function enforcedDateBounds(config: CustomFieldSpec["config"], timeZone?: string): ReturnType<typeof dateBoundsOf> {
  return isDateWindowClosed(config, timeZone) ? { earliest: null, latest: null } : dateBoundsOf(config);
}

/** Day-level on purpose: an instant edge's day stays pickable; Submit holds the time. */
export function isDayWithinDateBounds(day: Date, config: CustomFieldSpec["config"]): boolean {
  const { earliest, latest } = enforcedDateBounds(config);
  const local = localDayOf(day)!;
  if (earliest && local < localDayOf(boundValue(earliest))!) return false;
  if (latest && local > localDayOf(boundValue(latest))!) return false;
  return true;
}

function boundLabel(bound: DateBound): string {
  if (bound.kind === "today") {
    if (bound.days === 0) return "today";
    return bound.days > 0 ? `${countOf(bound.days, "day")} from today` : `${countOf(-bound.days, "day")} ago`;
  }
  if (isDayString(bound.value)) {
    const [y, m, d] = bound.value.split("-").map(Number);
    return new Date(y!, m! - 1, d!).toLocaleDateString(undefined, { dateStyle: "medium" });
  }
  return new Date(bound.value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function dayBounded(schema: z.ZodType, config: CustomFieldSpec["config"], timeZone?: string): z.ZodType {
  const { earliest, latest } = enforcedDateBounds(config, timeZone);
  const readable = (value: unknown) =>
    value == null || value === "" || localDayOf(value) !== null;
  const blank = (value: unknown) => value == null || value === "";
  let bounded = schema.refine(readable, { error: "Invalid date" });
  if (latest) {
    bounded = bounded.refine((value) => blank(value) || onSide(value, latest, "before", timeZone), {
      error:
        latest.kind === "today" && latest.days === 0
          ? "Can't be in the future"
          : `No later than ${boundLabel(latest)}`,
    });
  }
  if (earliest) {
    bounded = bounded.refine((value) => blank(value) || onSide(value, earliest, "after", timeZone), {
      error:
        earliest.kind === "today" && earliest.days === 0
          ? "Can't be in the past"
          : `No earlier than ${boundLabel(earliest)}`,
    });
  }
  return bounded;
}

/** The schema one custom field's value must satisfy.
 *
 *  Every field type stores "unanswered" as `null` or `undefined`, so each schema
 *  is nullish on the outside and its checks apply only to a real value. */
export function customFieldSchema(field: CustomFieldSpec, timeZone?: string): z.ZodType {
  const cap = customFieldMaxLength(field);

  switch (field.type) {
    case FIELD_TYPES.money: {
      // An amount is a number with at most the currency's minor-unit digits.
      // The editor commits a number; a numeric string (the form value of a
      // stored amount) is accepted too. Unlike `number`, unparseable text is
      // an error here, not "unanswered": no money editor can produce it, so
      // it marks a broken caller rather than a blank answer.
      const digits = currencyFractionDigits(field.config?.currency);
      return withBounds(
        moneyValue.refine(
          (value) => value == null || fractionDigitsOf(value) <= digits,
          { error: decimalsMessage(digits) },
        ),
        field.config,
      );
    }

    // A number that does not parse for its region is refused, like an email.
    // The refusal keeps the editor open with the typed digits, so the user
    // can correct them (the editor never clears its own draft). A cleared
    // number is unanswered, not invalid.
    case FIELD_TYPES.phone:
      return z
        .looseObject({ value: optionalText, countryCode: optionalText })
        .refine((value) => isValidPhoneValue(value), {
          error: "Invalid phone number",
        })
        .nullish();

    case FIELD_TYPES.address:
      return z
        .looseObject({
          addressLine1: optionalText,
          city: optionalText,
          state: optionalText,
          zip: optionalText,
          country: optionalText,
        })
        .nullish();

    case FIELD_TYPES.email:
      return capped(cap)
        // Clearing an email leaves "", which is unanswered, not invalid.
        .refine((value) => value === "" || isValidEmailText(value), {
          error: "Invalid email",
        })
        .nullish();

    case FIELD_TYPES.url:
      return capped(cap)
        // Clearing a URL leaves "", which is unanswered, not invalid. The
        // predicate is the one the editor's live ring reads.
        .refine((value) => value === "" || isValidUrlText(value), {
          error: "Invalid URL",
        })
        .nullish();

    case FIELD_TYPES.number: {
      // The editor rounds to the field's decimals on commit, so a value with
      // more marks a caller that skipped it. Whole numbers only allows none.
      const places = field.config?.wholeNumbers === true ? 0 : numberDecimalPlaces(field.config);
      return withBounds(
        numericValue.refine(
          (value) => value == null || fractionDigitsOf(value) <= places,
          { error: decimalsMessage(places) },
        ),
        field.config,
      );
    }

    case FIELD_TYPES.date:
    case FIELD_TYPES.dateTime:
      return dayBounded(z.unknown(), field.config, timeZone);

    case FIELD_TYPES.time:
      // The form layer hands over a Date; a stored value arrives as an ISO
      // string. Both are legitimate input, so the schema coerces.
      return z.coerce.date().nullish();

    case FIELD_TYPES.labels: {
      // A count no answer can meet gives way rather than hold Submit for good.
      const required = requiredChoicesOf(field.config);
      const offered = choiceOptionCount(field.config);
      const floor = countFloor(field.config);
      const min = floor === null ? null : Math.min(floor, offered);
      const cap = field.config?.max;
      const max = typeof cap === "number" && cap >= 1 ? Math.max(cap, required.length) : null;
      let ids = z.array(z.string());
      if (min !== null && min >= 1) ids = ids.min(min, { error: `Pick at least ${countOf(min, "choice")}` });
      if (max !== null) {
        ids = ids.max(max, { error: `Pick no more than ${countOf(max, "choice")}` });
      }
      const all = required.length === 0
        ? ids
        : ids.refine(
            (picked) => required.every((choice) => picked.includes(choice.value)),
            { error: `Include ${listOf(required.map((choice) => choice.label), "and")}` },
          );
      return all.nullish();
    }

    case FIELD_TYPES.select: {
      const required = requiredChoicesOf(field.config);
      const ids = z.array(z.string());
      return (
        required.length === 0
          ? ids
          : ids.refine(
              (picked) =>
                picked.length === 0 || required.some((choice) => picked.includes(choice.value)),
              { error: `Pick ${listOf(required.map((choice) => choice.label), "or")}` },
            )
      ).nullish();
    }

    case FIELD_TYPES.member:
      return z.array(z.string()).nullish();

    case FIELD_TYPES.photos: {
      const { min, max } = field.config ?? {};
      let ids = z.array(z.string());
      if (typeof min === "number") {
        ids = ids.min(min, { error: `Add at least ${countOf(min, "photo")}` });
      }
      if (typeof max === "number") {
        ids = ids.max(max, { error: `Add no more than ${countOf(max, "photo")}` });
      }
      return ids.nullish();
    }

    case FIELD_TYPES.checkbox:
      // Tri-state: null is unanswered and is not the same as false.
      return z.boolean().nullish();

    case FIELD_TYPES.paragraph: {
      // Stored as HTML; counts visible text so markup doesn't consume the budget.
      const floor = countFloor(field.config);
      return z
        .string()
        .refine(
          (value) => {
            const length = stripHtmlToText(value).length;
            return floor === null || length === 0 || length >= floor;
          },
          { error: `Min ${floor} characters` },
        )
        .refine(
          (value) => cap === undefined || stripHtmlToText(value).length <= cap,
          { error: `Max ${cap} characters` },
        )
        .nullish();
    }

    case FIELD_TYPES.text: {
      const floor = countFloor(field.config);
      const text = capped(cap);
      return (
        floor === null
          ? text
          : text.refine((value) => value === "" || value.length >= floor, { error: `Min ${floor} characters` })
      ).nullish();
    }

    default:
      // `files` and `region` carry no character or shape constraint,
      // and `formula` is computed — no editor produces a value to validate.
      // Validation must pass for them rather than fail closed.
      return z.unknown();
  }
}

export type CustomFieldValidationResult = {
  valid: boolean;
  message: string | null;
};

/** Validates one value and returns the first problem as a display string.
 *
 *  `ZodError.issues` is the only issue list in zod 4 — the v3 `.errors` alias is
 *  gone, and `.message` holds a JSON dump of the issues, not a sentence. */
export function validateCustomFieldValue(
  field: CustomFieldSpec,
  value: unknown,
  timeZone?: string,
): CustomFieldValidationResult {
  const result = customFieldSchema(field, timeZone).safeParse(value);
  if (result.success) return { valid: true, message: null };
  return {
    valid: false,
    message: result.error.issues[0]?.message ?? "Invalid value",
  };
}
